<template>
  <div>
    <modal
      :show.sync="showReservationHistoryModal"
      modal-classes="modal-dialog-centered modal-lg "
      body-classes="pt-1"
      @close="closeModalHistory()"
      no-close-on-backdrop
    >
      <template slot="header">
        <h4 class="subtitle d-flex align-items-center">
          <i class="flaticon-wall-clock-1 mr-2 d-inline-flex"></i>
          History - Reservation N° / ID:
          <span class="font-weight-400 f-16 ml-2"> {{ bookingNumber }}</span>
        </h4>
      </template>

      <div v-if="loader">
        <div class="text-center p-5">
          <h4><strong>Loading History...</strong></h4>

          <div id="loaderInner">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>

      <div class="box-timeline" v-else>
        <div
          class="timeline timeline-one-side"
          data-timeline-content="axis"
          data-timeline-axis-style="dashed"
        >
          <div v-for="data in histories" class="timeline-block">
            <span class="timeline-step badge" :class="getBadge(data.change)">
              <i :class="getIcon(data.change)"></i>
            </span>

            <div class="timeline-content">
              <div class="row">
                <div
                  class="col-xl-10 col-lg-10 col-md-10 col-sm-10 col-12 align-self-center"
                >
                  <div class="text-group">
                    <span class="text-min-flex">
                      {{
                        formatDate(
                          data.created_at,
                          getTimeZone(data.module.ref)
                        )
                      }}
                      at
                      {{
                        formatTime(
                          data.created_at,
                          getTimeZone(data.module.ref)
                        )
                      }}</span
                    >
                  </div>

                  <span class="title">{{ data.change }}</span>

                  <div class="text-group">
                    <span class="text-min-flex mt-5p">
                      <strong>User: </strong>{{ getUserName(data) }}.
                      <strong>Role: </strong> {{ getUserRole(data.role) }}.
                      <strong>Source: </strong>
                      {{ formatSource(data.source, data.role) }}</span
                    >
                  </div>
                </div>

                <div
                  class="col-xl-2 col-lg-2 col-md-2 col-sm-2 col-12 text-right align-self-center"
                >
                  <div
                    class="button-action"
                    v-if="!data.notes && canUserAddNote"
                  >
                    <span
                      class="link d-flex align-items-center f-12"
                      @click="addNote(data)"
                    >
                      <i class="flaticon-plus mr-1"> </i> Add Note
                    </span>
                  </div>
                </div>
              </div>

              <div class="review" v-if="data.attributes_modified.length > 0">
                <div class="card-updates">
                  <ul class="list-updates">
                    <li
                      v-for="(item, index) in data.attributes_modified"
                      :key="index"
                    >
                      <label>{{ item.attribute_name }}:</label>

                      <span class="old" v-if="item.oldValue"
                        >{{ item.oldValue }}
                      </span>
                      <span class="old" v-else>Empty </span> |
                      <span class="new"> {{ item.newValue }}</span>
                    </li>
                  </ul>
                </div>
              </div>

              <span v-if="data.notes" class="span-notes">
                <strong>Note:</strong> {{ data.notes }}</span
              >
            </div>
          </div>
          <base-alert
            type="warning"
            class="info-min"
            v-if="histories.length === 0"
          >
            <i class="flaticon-warning mr-1"></i> History not available
          </base-alert>
        </div>
      </div>

      <template slot="footer">
        <base-button
          type="outline-neutral mb-0"
          class="min-button"
          @click="closeModalHistory()"
          >Close</base-button
        >
      </template>
    </modal>

    <modal :show.sync="modalAddNotes" no-close-on-backdrop>
      <template slot="header">
        <h2 class="subtitle">Add Note</h2>
      </template>

      <div class="row">
        <div
          class="col-12 mt-3 mb-3"
          v-bind:class="{
            'is-invalid': $v.tempNote.$error,
          }"
        >
          <label class="title-label">Note</label>
          <el-input
            v-model="tempNote"
            maxlength="200"
            type="textarea"
            :autosize="{ minRows: 3, maxRows: 5 }"
            show-word-limit
          >
          </el-input>

          <small class="invalid-feedback" v-if="$v.tempNote.$error"
            >Enter a Note</small
          >
        </div>
      </div>

      <template slot="footer">
        <base-button
          type="outline-neutral mb-0"
          class="min-button"
          @click="closeModalNote()"
          >Close</base-button
        >

        <base-button
          type="primary ml-auto"
          class="min-button mb-0"
          @click="saveNote(tempItem._id, tempNote)"
          >Save</base-button
        >
      </template>
    </modal>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { ReservationsAPI } from "../../../core/api";
import { DropdownMenu, DropdownItem, Dropdown, MessageBox } from "element-ui";
import Swal from "sweetalert2";
import { TablePlugin } from "bootstrap-vue";
import { required, minLength, maxLength } from "vuelidate/lib/validators";
import { isUserAuthorized, setSigOut } from "../../../helpers/commons.helpers";
import {
  formatDate,
  formatSource,
  formatTime,
  getUserName,
  getUserRole,
} from "../../../core/services/history.services";

export default {
  name: "ModalReservationsHistory",
  props: {
    tempReservationId: {
      type: String,
      default: "",
      description: "id",
    },
    bookingNumber: {
      type: String,
      default: "",
      description: "booking number",
    },
    show: {
      type: Boolean,
    },
  },
  components: {
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [MessageBox.name]: MessageBox,
    TablePlugin,
  },
  data() {
    return {
      reservationId: null,
      records: [],
      modalAddNotes: false,
      tempNote: "",
      tempItem: [],
      showReservationHistoryModal: false,
      loader: false,
    };
  },
  watch: {
    show: {
      handler: function (current, old) {
        this.showReservationHistoryModal = current;
      },
      deep: true,
    },
    tempReservationId: {
      handler: async function (current, old) {
        this.reservationId = current;
        this.loader = true;
        await this.getHistoriesByReservationId(this.reservationId);
        this.loader = false;
      },
      deep: true,
    },
  },
  async created() {},
  methods: {
    getUserRole,
    formatDate,
    formatTime,
    formatSource,
    getUserName,
    async updateHistory(historyId, body) {
      try {
        this.setLoad(true);
        let { data } = await ReservationsAPI.updateHistoryById(
          historyId,
          body,
          this.sesion.token
        );
        this.setLoad(false);
        if (data) {
          this.$notify({
            type: "success",
            title: "Success!",
            message: "Your note has been saved succesfull",
            icon: "flaticon-check-3",
          });
          this.tempNote = "";
          this.tempItem = [];
          this.modalAddNotes = false;
          this.getHistoriesByReservationId(this.reservationId);
        } else {
          this.$notify({
            type: "danger",
            title: "Error!",
            message: "The Operation could not be completed. Please try again",
            icon: "flaticon-remove",
          });
        }
      } catch (error) {
        this.setLoad(false);
        this.handleError(
          error,
          "There was an error saving your reservation, please try again"
        );
      }
    },
    async saveNote(historyId, notes) {
      this.$v.tempNote.$touch();
      if (!this.$v.tempNote.$invalid) {
        let { value } = await Swal.fire({
          imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
          title: "Save Note",
          html: "Are you sure you save this note? <br> This note cannot be modified",
          buttonsStyling: false,
          showCloseButton: true,
          showCancelButton: true,
          focusConfirm: false,
          reverseButtons: true,
          cancelButtonText: "No",
          confirmButtonText: "Yes",
          confirmButtonClass: "btn btn-primary btn-small-one",
        });
        if (value) {
          this.updateHistory(historyId, { notes: notes });
        }
      } else {
        this.$notify({
          type: "warning",
          title: "Important!",
          message: "Please complete the required attributes",
          icon: "flaticon-warning-1",
        });
      }
    },
    handleError(error, message) {
      if (error.response) {
        let { data } = error.response;
        if (data.code === 401) {
          let auth = setSigOut(error);
          if (auth) {
            this.setSigOut(true);
          }
        } else {
          this.$notify({
            type: "danger",
            title: "Error!",
            message: "The Operation could not be completed. Please try again",
            icon: "flaticon-remove",
          });
        }
      } else {
        this.$notify({
          type: "danger",
          title: "Error!",
          message: message,
          icon: "flaticon-remove",
        });
      }
    },
    async getHistoriesByReservationId(reservationId) {
      try {
        let { data } = await ReservationsAPI.getHistoriesByReservationId(
          reservationId,
          100,
          1,
          this.sesion.token
        );
        if (data) {
          this.records = data.docs;
        } else {
          this.records = [];
        }
      } catch (error) {
        this.records = [];
        this.handleError(
          error,
          "There was an error saving your reservation, please try again"
        );
      }
    },
    addNote(item) {
      this.tempItem = item;
      this.modalAddNotes = true;
    },
    closeModalNote() {
      this.tempNote = "";
      this.tempItem = [];
      this.modalAddNotes = false;
    },
    closeModalHistory() {
      this.showReservationHistoryModal = false;
      this.$emit("close", false);
    },
    getIcon(status) {
      if (
        [
          "Reservation updated",
          "Reservation Updated",
          "Reservation Requoted",
        ].includes(status)
      ) {
        return "flaticon-pencil";
      } else if (status === "Reservation created") {
        return "flaticon-plus";
      } else if (status === "Reservation Cancelled without refund") {
        return "flaticon-remove ";
      } else if (status === "Reservation Confirmed") {
        return "flaticon-bold-check-3";
      } else if (status === "Resend Notifications") {
        return "flaticon-envelope";
      } else if (status === "Reservation Paid") {
        return "flaticon-invoice";
      } else if (status === "Rejected Reservation") {
        return "flaticon-remove";
      } else if (status === "Reservation No Show") {
        return "flaticon-no-group";
      } else if (status === "Payment Window Ended") {
        return "flaticon-remove ";
      } else {
        return "flaticon-rec";
      }
    },
    getBadge(status) {
      if (
        [
          "Reservation updated",
          "Reservation Updated",
          "Reservation Requoted",
        ].includes(status)
      ) {
        return "bg-updated";
      } else if (status === "Reservation created") {
        return "bg-new";
      } else if (status === "Reservation Cancelled without refund") {
        return "bg-cancelled";
      } else if (status === "Reservation Confirmed") {
        return "bg-confirmed";
      } else if (status === "Resend Notifications") {
        return "bg-started";
      } else if (status === "Reservation Paid") {
        return "bg-confirmed";
      } else if (status === "Rejected Reservation") {
        return "bg-rejected";
      } else if (status === "Reservation No Show") {
        return "bg-noshow";
      } else if (status === "Payment Window Ended") {
        return "bg-cancelled";
      } else {
        return "bg-draft";
      }
    },
    changeItemPosition(items = [], currentPosition, newPosition) {
      if (currentPosition === newPosition || currentPosition < 0) {
        return items;
      } else {
        let [item] = items.splice(currentPosition, 1);
        items.splice(newPosition, 0, item);
        return items;
      }
    },
    getEventIndex(histories = [], event) {
      return histories.map((history) => history.change).indexOf(event);
    },
    getTimeZone(reservation) {
      if (reservation.class === "Experiences") {
        return reservation.product.timeZone;
      } else {
        return reservation.branch.timezone;
      }
    },
    ...mapMutations(["setLoad", "setSigOut"]),
  },
  computed: {
    ...mapGetters(["sesion", "userRole"]),
    canUserAddNote() {
      return isUserAuthorized(
        "RESERVATIONS",
        this.userRole,
        "ADD_HISTORY_NOTE"
      );
    },
    histories: function () {
      let histories = this.records;
      let eventIndex = this.getEventIndex(histories, "Reservation created");
      return this.changeItemPosition(
        histories,
        eventIndex,
        histories.length - 1
      );
    },
  },
  validations: {
    tempNote: {
      required,
      minLength: minLength(5),
      maxLength: maxLength(200),
    },
  },
};
</script>
